import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { safeLocalStorage } from "@/lib/storage";
import { useIsHydrated } from "./useIsHydrated";
export const SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX = "codesmith-sidebar-automation-rows:";
// Global, not per org, so the cache stores expanded rows and readers apply
// this at read time.
export const AUTOMATIONS_EXPANDED_STORAGE_KEY = "assistantAutomationsExpanded";
// Same org-less fallback as useCachedOrgFlag: early renders and the skeletons
// can run before the route's org is parsed.
const LAST_RESOLVED_ORG = "__last__";
const NO_SECTION = "none";

/**
 * Group rows the sidebar's Automations section opens with when expanded, from
 * last visit: `null` means no section, e.g. 20 groups → 15.
 */
export function readCachedSidebarAutomationRows(orgName: string | null): number | null {
  const raw = safeLocalStorage.get(SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX + (orgName ?? LAST_RESOLVED_ORG));
  const rows = raw === null ? NaN : Number(raw);
  return Number.isInteger(rows) && rows >= 0 ? rows : null;
}

/**
 * The Automations section's row count, falling back to the last visit's
 * until the automation groups query answers, and caching it once it does so
 * the next load's skeleton reserves the same footprint.
 */
export function useCachedSidebarAutomationRows(t0) {
  const $ = _c(7);
  const {
    orgName,
    rows,
    rowsKnown
  } = t0;
  const isHydrated = useIsHydrated();
  let t1;
  let t2;
  if ($[0] !== orgName || $[1] !== rows || $[2] !== rowsKnown) {
    t1 = () => {
      if (!rowsKnown || !orgName) {
        return;
      }
      const value = rows === null ? NO_SECTION : String(rows);
      safeLocalStorage.set(SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX + orgName, value);
      safeLocalStorage.set(SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX + LAST_RESOLVED_ORG, value);
    };
    t2 = [orgName, rows, rowsKnown];
    $[0] = orgName;
    $[1] = rows;
    $[2] = rowsKnown;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (rowsKnown) {
    return rows;
  }
  if (!isHydrated) {
    return null;
  }
  let t3;
  if ($[5] !== orgName) {
    t3 = readCachedSidebarAutomationRows(orgName);
    $[5] = orgName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}